import type { BoardColumn } from '@hugo/core'
import { PlusIcon, XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { COLUMN_LABELS, composerKind } from '@/lib/board'
import { formatMinutes, kindLabel } from '@/lib/format'
import { useRunTool, useSettings } from '@/lib/items'
import { defaultMinutes, ESTIMATE_CHOICES } from '@/lib/plan'
import { CapturePreview, useCapturePreview } from './capture-preview'

/** "Add a card" at the end of the cards the user put in a column. */
export function AddCardButton({ column, onClick }: { column: BoardColumn; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="add-card-button"
      data-column={column}
      aria-label={`Add a card to ${COLUMN_LABELS[column]}`}
      className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-sm text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
    >
      <PlusIcon aria-hidden="true" className="size-4" />
      Add a card
    </button>
  )
}

/**
 * A new card right in a column: typed like a capture ("Send Anna the contract by Fri": I owe
 * Anna, due Friday) with the time it needs. Enter adds it and keeps the composer open for the
 * next one; Esc closes it. In Waiting the card is a waiting-on item; in the other columns it is
 * planned for the column's day or week. One undo removes it.
 */
export function BoardComposer({
  column,
  timeZone,
  onClose,
}: {
  column: BoardColumn
  timeZone: string
  onClose: () => void
}) {
  const { run } = useRunTool()
  const settings = useSettings()
  const [text, setText] = useState('')
  const [estimate, setEstimate] = useState('')
  const [saving, setSaving] = useState(false)
  const field = useRef<HTMLTextAreaElement>(null)
  const preview = useCapturePreview(text)
  const label = COLUMN_LABELS[column]

  useEffect(() => {
    field.current?.focus()
  }, [])

  const submit = async () => {
    const trimmed = text.trim()
    if (!trimmed || saving) return
    setSaving(true)
    const result = await run(
      'item.capture',
      {
        text: trimmed,
        ...(estimate ? { estimateMinutes: Number(estimate) } : {}),
        place: { column: column === 'done' ? 'today' : column },
      },
      {
        done: (r) =>
          column === 'waiting'
            ? `Added to ${label}`
            : `Added to ${label} · ${kindLabel(r.item.kind)}`,
      },
    )
    setSaving(false)
    if (result) {
      setText('')
      setEstimate('')
    }
    field.current?.focus()
  }

  return (
    <form
      data-testid="add-card"
      data-column={column}
      className="flex flex-col gap-2 rounded-lg border bg-card p-2 shadow-xs"
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      <Textarea
        ref={field}
        rows={2}
        value={text}
        maxLength={1000}
        aria-label={`New card in ${label}`}
        placeholder="e.g. Send Anna the contract by Fri"
        className="min-h-0 resize-none"
        onChange={(event) => setText(event.target.value.replace(/\n/g, ' '))}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            void submit()
          } else if (event.key === 'Escape') {
            event.preventDefault()
            event.stopPropagation()
            onClose()
          }
        }}
      />
      {preview ? (
        <div
          className="flex min-w-0 flex-wrap items-center gap-1 text-xs"
          data-testid="add-card-preview"
          aria-live="polite"
        >
          <CapturePreview
            preview={preview}
            timeZone={timeZone}
            kind={composerKind(column, preview.kind)}
            showTitle={false}
          />
        </div>
      ) : null}
      <div className="flex items-center gap-1.5">
        <NativeSelect
          aria-label="Time needed"
          title="Time needed (≈: hugo's default)"
          value={estimate}
          size="sm"
          className="min-w-0 flex-1"
          onChange={(event) => setEstimate(event.target.value)}
        >
          {/* A new card has no type yet: hugo assumes the default for that, as cards show it. */}
          <NativeSelectOption value="">
            ≈{' '}
            {formatMinutes(
              defaultMinutes({ type: null, plan: null }, settings.data?.rules.estimateMinutes),
            )}
          </NativeSelectOption>
          {ESTIMATE_CHOICES.map((minutes) => (
            <NativeSelectOption key={minutes} value={minutes}>
              {formatMinutes(minutes)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <Button type="submit" size="sm" disabled={!text.trim() || saving}>
          Add card
        </Button>
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Close" onClick={onClose}>
          <XIcon aria-hidden="true" />
        </Button>
      </div>
      <p className="text-xs text-muted-foreground max-md:hidden">
        <Kbd>↵</Kbd> to add · <Kbd>Esc</Kbd> to close
      </p>
    </form>
  )
}
